import React, { useCallback, useEffect, useMemo, useState } from "react"
import { Button } from "@/components/ui/button"
import { ChevronDown, ChevronUp } from "lucide-react"
import { AlternativeRoute } from "@/lib/planRouteParser"
import { Route, Toll } from "@/components/maps/RouteView"
import { getCountryFlag, getCountryName } from "./mapUtils"
import { getRouteColorsFromLabel } from "@/lib/routeColors"
import { useIsMobile } from "@/hooks/use-mobile"
import { useTranslation } from "react-i18next"
import { getIntlLocale } from "@/lib/i18n/format"

const ROUTE_LABEL_KEYS = {
  cheapest: "maps.alternativeRouteSelector.cheapest",
  shortest: "maps.alternativeRouteSelector.shortest",
  fastest: "maps.alternativeRouteSelector.fastest",
  recommended: "maps.alternativeRouteSelector.recommended",
  economic: "maps.alternativeRouteSelector.economic",
} as const

type RouteOption = {
  id: string
  name: string
  route: Route
  isSelected: boolean
  duration: number
  distance: number
  fuel: number
  tolls: number
  routeHandles: string[]
}

type AlternativeRouteSelectorProps = {
  selectedRouteId: string | undefined
  alternativeRoutes: AlternativeRoute[] | undefined
  onRouteSelect?: (routeId: string) => void
  onRouteHover?: (routeId: string | undefined) => void
  hoveredRouteId?: string | undefined
  className?: string
  disabled?: boolean
  /**
   * When false, all fuel-related columns and values are hidden.
   * Defaults to `true`. Set to `false` for non-HERE routing providers.
   */
  showFuel?: boolean
}

// Helper function to get duration and distance from alternative route
const getRouteMetrics = (altRoute: AlternativeRoute) => {
  const route = altRoute.routeInfo.routes[0]
  const stops = altRoute.stops
  const lastStop = stops?.[stops.length - 1]

  const duration =
    lastStop?.remaining_time ||
    route?.legs.reduce((sum, leg) => sum + leg.duration, 0) ||
    0

  const distance = lastStop?.remaining_distance || route?.distance || 0

  return { duration, distance }
}

const AlternativeRouteSelector: React.FC<AlternativeRouteSelectorProps> = ({
  selectedRouteId,
  alternativeRoutes,
  onRouteSelect,
  onRouteHover,
  hoveredRouteId,
  className = "",
  disabled = false,
  showFuel = true,
}) => {
  const { t } = useTranslation()
  // Persist expanded state in localStorage
  const [isDesktopExpanded, setIsDesktopExpanded] = useState(() => {
    const saved = localStorage.getItem("alternativeRouteSelector.isExpanded")
    return saved !== null ? JSON.parse(saved) : true
  })
  // On a phone the expanded table would cover the whole map, so it starts
  // collapsed to a one-line pill there and the choice isn't persisted — the
  // desktop preference stays untouched.
  const isMobile = useIsMobile()
  const [isMobileExpanded, setIsMobileExpanded] = useState(false)
  const isExpanded = isMobile ? isMobileExpanded : isDesktopExpanded
  const setIsExpanded = isMobile ? setIsMobileExpanded : setIsDesktopExpanded

  const [showTollsDetails, setShowTollsDetails] = useState(() => {
    const saved = localStorage.getItem(
      "alternativeRouteSelector.showTollsDetails",
    )
    return saved !== null ? JSON.parse(saved) : true
  })

  // Persist expanded state to localStorage
  useEffect(() => {
    localStorage.setItem(
      "alternativeRouteSelector.isExpanded",
      JSON.stringify(isDesktopExpanded),
    )
  }, [isDesktopExpanded])

  // Persist tolls details state to localStorage
  useEffect(() => {
    localStorage.setItem(
      "alternativeRouteSelector.showTollsDetails",
      JSON.stringify(showTollsDetails),
    )
  }, [showTollsDetails])

  // Calculate toll cost
  const calculateTollCost = useCallback((tolls: Toll[] | undefined): number => {
    if (!tolls) return 0
    return tolls.reduce((total, toll) => total + toll.price, 0)
  }, [])

  // Currency formatting function (reused from TollsInformation.tsx)
  const formatCurrency = (amount: number, currency: string): string => {
    const formatter = new Intl.NumberFormat(getIntlLocale(undefined), {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    return formatter.format(amount)
  }

  // Create route options with intelligent naming based on actual characteristics
  const routeOptions = useMemo((): RouteOption[] => {
    if (!alternativeRoutes?.length) return []

    // Define type for route analysis
    type RouteAnalysis = {
      id: string
      route: Route
      index: number
      duration: number
      distance: number
      fuel: number
      tolls: number
      routeHandles: string[]
    }

    // First, collect all routes with their characteristics
    const allRoutes: RouteAnalysis[] = []

    alternativeRoutes.forEach((altRoute, index) => {
      const route = altRoute.routeInfo.routes[0]
      if (!route) return

      // Get total distance and duration using the helper function with fallback logic
      const { duration: totalDuration, distance: totalDistance } =
        getRouteMetrics(altRoute)
      const totalTolls = calculateTollCost(route.tolls)
      const fuelConsumption = altRoute.fuel || 0 // Get fuel from alternativeRoute level

      allRoutes.push({
        id: route.id ?? `route-${index}`,
        route,
        index,
        duration: totalDuration,
        distance: totalDistance,
        fuel: fuelConsumption,
        tolls: totalTolls,
        routeHandles: altRoute.stops.map((stop) => stop.route_handle),
      })
    })

    if (allRoutes.length === 0) return []

    // Create the final options array
    return allRoutes.map((routeData) => {
      // Check if this is a custom route
      const isCustomRoute = routeData.route.isCustom
      const label = routeData.route.label ?? ""
      const labelKey =
        ROUTE_LABEL_KEYS[label.toLowerCase() as keyof typeof ROUTE_LABEL_KEYS]
      const routeName = isCustomRoute
        ? t("maps.alternativeRouteSelector.custom")
        : labelKey
          ? t(labelKey)
          : label

      return {
        id: routeData.id,
        name: routeName,
        route: routeData.route,
        isSelected: selectedRouteId === routeData.id,
        duration: routeData.duration,
        distance: routeData.distance,
        fuel: routeData.fuel,
        tolls: routeData.tolls,
        routeHandles: routeData.routeHandles,
      }
    })
  }, [selectedRouteId, alternativeRoutes, calculateTollCost, t])

  // Get selected route for comparison
  const selectedRouteOption = routeOptions.find((option) => option.isSelected)

  // Format time in hours and minutes
  const formatDuration = (durationInSeconds: number): string => {
    const hours = Math.floor(durationInSeconds / 3600)
    const minutes = Math.floor((durationInSeconds % 3600) / 60)
    return `${hours} h ${minutes} min`
  }

  // Format distance in km
  const formatDistance = (distanceInMeters: number): string => {
    const km = Math.round(distanceInMeters / 1000)
    return `${km} km`
  }

  // Format toll cost
  const formatTollCost = (cost: number): string => {
    return `${cost.toFixed(0)} EUR`
  }

  // Format fuel consumption in liters
  const formatFuelConsumption = (liters: number): string => {
    return `${liters.toFixed(1)}L`
  }

  // Calculate differences relative to selected route
  const calculateDifference = (
    value: number,
    baseValue: number,
    isPositive: boolean = false,
  ): { diff: string; isNegative: boolean } => {
    const difference = value - baseValue
    const isNegative = difference < 0
    const sign = difference > 0 ? "+" : ""
    return {
      diff: `${sign}${difference.toFixed(0)}`,
      isNegative: isNegative !== isPositive,
    }
  }

  // Calculate time difference in minutes
  const calculateTimeDifference = (
    duration: number,
    baseDuration: number,
  ): { diff: string; isNegative: boolean } => {
    const diffInMinutes = Math.round((duration - baseDuration) / 60)
    const sign = diffInMinutes > 0 ? "+" : ""
    return {
      diff: `${sign}${diffInMinutes} min`,
      isNegative: diffInMinutes < 0,
    }
  }

  // Calculate distance difference in km
  const calculateDistanceDifference = (
    distance: number,
    baseDistance: number,
  ): { diff: string; isNegative: boolean } => {
    const diffInKm = Math.round((distance - baseDistance) / 1000)
    const sign = diffInKm > 0 ? "+" : ""
    return {
      diff: `${sign}${diffInKm} km`,
      isNegative: diffInKm < 0,
    }
  }

  // Handle route selection
  const handleRouteSelect = (routeId: string) => {
    if (!disabled && onRouteSelect) {
      onRouteSelect(routeId)
    }
  }

  // Group tolls by country and currency (reused from TollsInformation.tsx)
  const groupTollsByCountry = (tolls: Toll[] | undefined) => {
    if (!tolls) return {}
    return tolls.reduce<Record<string, Record<string, number>>>((acc, toll) => {
      if (!acc[toll.country_code]) {
        acc[toll.country_code] = {}
      }

      if (!acc[toll.country_code][toll.currency]) {
        acc[toll.country_code][toll.currency] = 0
      }

      acc[toll.country_code][toll.currency] += toll.price
      return acc
    }, {})
  }

  const tollsByCountry = groupTollsByCountry(selectedRouteOption?.route.tolls)

  if (!routeOptions.length) {
    return null
  }

  // Fixed pixel columns need ~480px; a phone-width map panel is ~340px, so
  // there the value columns size to their content and the name truncates.
  const gridColumns = isMobile
    ? showFuel
      ? "minmax(0,1fr) auto auto auto auto"
      : "minmax(0,1fr) auto auto auto"
    : showFuel
      ? "1fr 100px 100px 80px 100px"
      : "1fr 100px 100px 100px"
  const valueText = isMobile ? "text-xs" : "text-sm"
  const gridGap = isMobile ? "gap-x-3" : ""

  return (
    <div
      // z-10 keeps it above MapLibre's controls (z-index 2). Phone: the
      // collapsed pill sits to the right of the bottom-left zoom control and
      // hugs the bottom edge; expanded it spans the map's width.
      className={`absolute z-10 rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800 ${
        !isMobile
          ? "bottom-4 left-1"
          : isExpanded
            ? "bottom-1 left-1 right-1"
            : "bottom-1 left-14 max-w-[calc(100%-6.5rem)]"
      } ${className}`}
      onMouseLeave={() => !disabled && onRouteHover?.(undefined)}
    >
      {/* Collapsed header */}
      <div
        className={`flex cursor-pointer items-center justify-between ${
          isMobile ? "p-2" : "p-3"
        }`}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {(!isMobile || isExpanded) && (
            <span className="shrink-0 text-sm font-medium text-gray-700 dark:text-gray-300">
              {isExpanded
                ? t("maps.alternativeRouteSelector.chosenRoute")
                : t("maps.alternativeRouteSelector.routeLabel")}
            </span>
          )}
          <span className="shrink-0 text-sm font-medium text-gray-900 dark:text-gray-100">
            {selectedRouteOption?.name ||
              t("maps.alternativeRouteSelector.route")}
          </span>
          {!isExpanded && selectedRouteOption && (
            <span className="truncate text-xs text-gray-600 dark:text-gray-400">
              ({formatDuration(selectedRouteOption.duration)} •{" "}
              {formatDistance(selectedRouteOption.distance)}
              {showFuel
                ? ` • ${formatFuelConsumption(selectedRouteOption.fuel)}`
                : ""}{" "}
              • {formatTollCost(selectedRouteOption.tolls)})
            </span>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {isExpanded && (
            <Button
              variant="outline"
              size="sm"
              className="h-7 px-2 py-1 text-xs"
              onClick={(e) => {
                e.stopPropagation()
                setShowTollsDetails(!showTollsDetails)
              }}
            >
              {showTollsDetails
                ? t("maps.alternativeRouteSelector.hideTollsDetails")
                : t("maps.alternativeRouteSelector.showTollsDetails")}
            </Button>
          )}
          <div className="p-1">
            {isExpanded ? (
              <ChevronDown className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            ) : (
              <ChevronUp className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            )}
          </div>
        </div>
      </div>

      {/* Expanded content */}
      {isExpanded && (
        <div
          className={`bg-base border-t border-gray-200 dark:border-gray-700 ${
            isMobile ? "max-h-[45vh] overflow-y-auto" : ""
          }`}
        >
          <div className={isMobile ? "p-3" : "p-4"}>
            {/* Route options */}
            <div className="space-y-2">
              {/* Headers */}
              <div
                className={`grid border-b border-gray-100 pb-2 text-xs font-medium text-gray-500 dark:border-gray-600 dark:text-gray-400 ${gridGap}`}
                style={{
                  gridTemplateColumns: gridColumns,
                }}
              >
                <div></div>
                <div className="text-right">
                  {t("maps.alternativeRouteSelector.drivingTime")}
                </div>
                <div className="text-right">
                  {t("maps.alternativeRouteSelector.distance")}
                </div>
                {showFuel && (
                  <div className="text-right">
                    {t("maps.alternativeRouteSelector.fuel")}
                  </div>
                )}
                <div className="text-right">
                  {t("maps.alternativeRouteSelector.tolls")}
                </div>
              </div>

              {/* Route options */}
              {routeOptions.map((option) => {
                const isBaseline = option.isSelected
                const selectedOption = routeOptions.find(
                  (opt) => opt.isSelected,
                )

                const timeDiff =
                  selectedOption && !isBaseline
                    ? calculateTimeDifference(
                        option.duration,
                        selectedOption.duration,
                      )
                    : null
                const distanceDiff =
                  selectedOption && !isBaseline
                    ? calculateDistanceDifference(
                        option.distance,
                        selectedOption.distance,
                      )
                    : null
                const tollDiff =
                  selectedOption && !isBaseline
                    ? calculateDifference(option.tolls, selectedOption.tolls)
                    : null
                const fuelDiff =
                  selectedOption && !isBaseline
                    ? calculateDifference(option.fuel, selectedOption.fuel)
                    : null

                const routeColors = getRouteColorsFromLabel(option.route.label)
                const isHovered = hoveredRouteId === option.id && isExpanded

                return (
                  <div
                    key={option.id}
                    className={`grid cursor-pointer items-center rounded px-1 py-2 transition-colors ${gridGap} ${
                      option.isSelected
                        ? "bg-sidebar-accent"
                        : isHovered
                          ? "bg-yellow-100 dark:bg-yellow-800/30"
                          : "hover:bg-gray-100 dark:hover:bg-gray-600/60"
                    }`}
                    onClick={() => handleRouteSelect(option.id)}
                    onMouseEnter={() => onRouteHover?.(option.id)}
                    onMouseLeave={() => onRouteHover?.(undefined)}
                    style={{
                      gridTemplateColumns: gridColumns,
                    }}
                  >
                    {/* Route name with radio button and color indicator */}
                    <div className="flex min-w-0 items-center">
                      <div
                        className={`mr-3 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
                          option.isSelected
                            ? "border-blue-500 bg-blue-500"
                            : "border-gray-400 hover:border-blue-400 dark:border-gray-500"
                        }`}
                      >
                        {option.isSelected && (
                          <div className="h-2 w-2 rounded-full bg-white"></div>
                        )}
                      </div>
                      {/* Color indicator bar */}
                      <div
                        className="mr-1 h-[1.15em] w-[3px] shrink-0"
                        style={{
                          backgroundColor: routeColors.main,
                        }}
                      />
                      <span
                        className={`truncate ${valueText} font-medium text-gray-900 dark:text-gray-100`}
                      >
                        {option.name}
                      </span>
                    </div>

                    {/* Duration */}
                    <div className="text-right">
                      {isBaseline ? (
                        <div
                          className={`font-mono ${valueText} font-medium text-gray-900 dark:text-gray-100`}
                        >
                          {formatDuration(option.duration)}
                        </div>
                      ) : (
                        timeDiff && (
                          <div
                            className={`font-mono ${valueText} font-medium ${
                              timeDiff.isNegative
                                ? "text-green-700 dark:text-green-500"
                                : "text-destructive"
                            }`}
                          >
                            {timeDiff.diff}
                          </div>
                        )
                      )}
                    </div>

                    {/* Distance */}
                    <div className="text-right">
                      {isBaseline ? (
                        <div
                          className={`font-mono ${valueText} font-medium text-gray-900 dark:text-gray-100`}
                        >
                          {formatDistance(option.distance)}
                        </div>
                      ) : (
                        distanceDiff && (
                          <div
                            className={`font-mono ${valueText} font-medium ${
                              distanceDiff.isNegative
                                ? "text-green-700 dark:text-green-500"
                                : "text-destructive"
                            }`}
                          >
                            {distanceDiff.diff}
                          </div>
                        )
                      )}
                    </div>

                    {/* Fuel */}
                    {showFuel && (
                      <div className="text-right">
                        {isBaseline ? (
                          <div
                            className={`font-mono ${valueText} font-medium text-gray-900 dark:text-gray-100`}
                          >
                            {formatFuelConsumption(option.fuel)}
                          </div>
                        ) : (
                          fuelDiff && (
                            <div
                              className={`font-mono ${valueText} font-medium ${
                                fuelDiff.isNegative
                                  ? "text-green-700 dark:text-green-500"
                                  : "text-destructive"
                              }`}
                            >
                              {fuelDiff.diff}L
                            </div>
                          )
                        )}
                      </div>
                    )}

                    {/* Tolls */}
                    <div className="text-right">
                      {isBaseline ? (
                        <div
                          className={`font-mono ${valueText} font-medium text-gray-900 dark:text-gray-100`}
                        >
                          {formatTollCost(option.tolls)}
                        </div>
                      ) : (
                        tollDiff && (
                          <div
                            className={`font-mono ${valueText} font-medium ${
                              tollDiff.isNegative
                                ? "text-green-700 dark:text-green-500"
                                : "text-destructive"
                            }`}
                          >
                            {tollDiff.diff} EUR
                          </div>
                        )
                      )}
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Country toll breakdown */}
            {showTollsDetails && Object.keys(tollsByCountry).length > 0 && (
              <div className="mt-6 border-t border-gray-200 pt-4 dark:border-gray-700">
                <div className="space-y-2">
                  {Object.entries(tollsByCountry)
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([countryCode, currencyMap]) => (
                      <div
                        key={countryCode}
                        className="flex items-center justify-between text-sm"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-base">
                            {getCountryFlag(countryCode)}
                          </span>
                          <span className="font-medium text-gray-700 dark:text-gray-300">
                            {getCountryName(countryCode)}
                          </span>
                        </div>
                        <div className="text-right">
                          {Object.entries(currencyMap).map(
                            ([currency, amount]) => (
                              <div
                                key={`${countryCode}-${currency}`}
                                className="font-medium text-gray-900 dark:text-gray-100"
                              >
                                {formatCurrency(amount, currency)}
                              </div>
                            ),
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default AlternativeRouteSelector
